import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useState, ReactNode } from 'react';

import LoadingSpinner from './LoadingSpinner';

interface ConfirmableButtonProps {
  onConfirm: () => void | Promise<unknown>;
  children: ReactNode;
  buttonClassName?: string;
  confirmButtonClassName?: string;
  cancelButtonClassName?: string;
  containerClassName?: string;
  onShowConfirmation?: (show: boolean) => void;
  confirmationMessage?: string;
  disabled?: boolean;
  tooltip?: string;
}

const ConfirmableButton: React.FC<ConfirmableButtonProps> = ({
  onConfirm,
  children,
  buttonClassName = '',
  confirmButtonClassName = 'p-1 bg-white rounded-full border border-gray-300 text-green-600 hover:text-green-800 hover:border-green-600',
  cancelButtonClassName = 'p-1 bg-white rounded-full border border-gray-300 text-gray-600 hover:text-gray-800 hover:border-gray-600',
  containerClassName = 'flex gap-1',
  onShowConfirmation,
  confirmationMessage,
  disabled = false,
  tooltip,
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleShowConfirmation = () => {
    if (disabled) return;
    setShowConfirmation(true);
    if (onShowConfirmation) {
      onShowConfirmation(true);
    }
  };

  const handleConfirm = async () => {
    if (disabled || isLoading) return;
    setIsLoading(true);
    try {
      await onConfirm();
    } finally {
      setIsLoading(false);
      setShowConfirmation(false);
      if (onShowConfirmation) {
        onShowConfirmation(false);
      }
    }
  };

  const handleCancel = () => {
    if (isLoading) return;
    setShowConfirmation(false);
    if (onShowConfirmation) {
      onShowConfirmation(false);
    }
  };

  if (!showConfirmation) {
    return (
      <button
        onClick={handleShowConfirmation}
        className={buttonClassName}
        disabled={disabled}
        title={tooltip}
      >
        {children}
      </button>
    );
  }

  return (
    <div className='flex flex-col items-center gap-2'>
      {confirmationMessage && (
        <span className='text-sm text-gray-700'>{confirmationMessage}</span>
      )}
      <div className={containerClassName}>
        <button
          onClick={handleConfirm}
          className={confirmButtonClassName}
          title='Confirm'
          disabled={disabled || isLoading}
        >
          {isLoading ? (
            <LoadingSpinner color='border-current' />
          ) : (
            <CheckIcon className='w-4 h-4' />
          )}
        </button>
        <button
          onClick={handleCancel}
          className={cancelButtonClassName}
          title='Cancel'
          disabled={disabled || isLoading}
        >
          <XMarkIcon className='w-4 h-4' />
        </button>
      </div>
    </div>
  );
};

export default ConfirmableButton;
